/**
 * The wire types for the shell settings that follow a user between browsers:
 * their theme, their appearance, and — the point of LPN-1 — their tabs and
 * windows.
 *
 * Almost everything here is deliberately opaque. The values are lunula's own
 * persistence blobs, produced and consumed by the toolkit at one end and stored
 * as text at the other; no LunaPin module parses one. That is what keeps a
 * toolkit upgrade which adds a field to a snapshot from being a change to this
 * file, to the database, and to the route between them.
 *
 * What is *not* opaque is which keys are allowed to travel — see [UiSettingKeys].
 *
 * @see ApiRoutes.USER_UI_SETTINGS
 */
package se.soderbjorn.lunapin.clientserver

import kotlinx.serialization.Serializable

/**
 * The persistence keys the server will store, spelled out.
 *
 * The toolkit ones are string copies of `se.soderbjorn.lunula.core.PersistKeys`
 * constants, and they are copies because this module has no business depending on
 * the toolkit: :clientServer is the wire, shared with the JVM server, which has
 * no browser shell in it at all. `PersistKeysMatchToolkitTest` in :web — the one
 * module that sees both — asserts the copies still match, so a rename upstream
 * fails a test rather than silently stranding everyone's stored layout under a key
 * nothing reads any more.
 *
 * The set is an allowlist, enforced server-side. The alternative — storing
 * whatever key the client names — would turn a settings table into a per-user
 * scratch space that any browser could fill, and would make the question "what
 * does the server keep about me" unanswerable from the code.
 */
object UiSettingKeys {
    /**
     * `lunapin.workspace.v1` — the caller's tabs and the windows in them.
     *
     * **LunaPin's own key, and the one LPN-1 exists for.** The toolkit's
     * [LAYOUT_STATE] below carries where the windows *sit*; nothing upstream knows
     * what a LunaPin window *is*. What round-trips here is exactly that — the
     * ordered tabs, and per window its kind. Today there is one kind, so the blob
     * is little more than a shape with room in it; the shape is the part that
     * matters, because LPN-2's bookmark windows are a case in it rather than a new
     * key, a new route and a new migration.
     *
     * One key for the whole workspace rather than one per tab: it arrives and
     * departs in one read and one write, so a tab reorder is a single fact rather
     * than a fan of them that could half-apply.
     */
    const val WORKSPACE: String = "lunapin.workspace.v1"

    /**
     * `lunapin.starredFilters.v1` — the filters the user has starred (LPN-68).
     *
     * LunaPin's own key, like [WORKSPACE], and separate from it for the reason the
     * toolkit keeps theme favourites apart from the theme selection: starring answers
     * "which filters do I want to get back to", not "what is open", so it must not
     * be rewritten by — or lost with — a rearrangement of tabs and windows.
     */
    const val STARRED_FILTERS: String = "lunapin.starredFilters.v1"

    /**
     * `darkness.layoutState` — where the windows sit inside each tab.
     *
     * A toolkit key, and opaque here: per-tab layout preset, window order, and
     * each window's rectangle, z-order and maximized state. Stored because a
     * workspace that forgets where you dragged things is not one — the ticket asks
     * for the *layout* of tabs and windows to be persisted, and this key is half
     * of that answer ([WORKSPACE] being the other half).
     */
    const val LAYOUT_STATE: String = "darkness.layoutState"

    /**
     * `darkness.theme.v2.selection` — the appearance (Auto/Dark/Light) and the
     * theme chosen for each of the dark and light slots.
     */
    const val THEME_SELECTION: String = "darkness.theme.v2.selection"

    /**
     * `darkness.theme.v2.custom` — themes the user built in the theme manager.
     *
     * Persisted together with the selection because the selection *names* one of
     * these by string. Storing the choice without the theme it points at would be
     * a reference to nothing: on the next load the toolkit would find an unknown
     * name and fall back to a built-in, so the user would have kept their
     * appearance and lost their colours.
     */
    const val THEME_CUSTOM: String = "darkness.theme.v2.custom"

    /**
     * `darkness.appearance.shape` — corner roundness, spacing density and
     * selection style. Its own key upstream precisely so a theme change cannot
     * carry them along or clear them; mirrored here for the same reason.
     */
    const val APPEARANCE_SHAPE: String = "darkness.appearance.shape"

    /**
     * `darkness.appearance.fonts` — which face and size the user picked for each
     * lettered surface: the sidebar and topbar, the tab strip, window titles,
     * prose, headings, and monospaced content.
     *
     * One blob rather than twelve keys, and that is the toolkit's decision, not
     * this file's — see `AppearanceFonts` upstream, which argues it and states
     * what it costs. What matters here is that the allowlist pays for a font
     * feature once instead of once per surface.
     *
     * Stored for the same reason [APPEARANCE_SHAPE] is, and the two are the same
     * kind of answer: a theme says what the app looks like, while a face says how
     * this person likes to read, and the second has to outlive every change to
     * the first. Without this key the Appearance sidebar was a control that
     * worked and then didn't — the pick repainted instantly and was gone on the
     * next load, which reads as the app losing things rather than as a setting
     * that was never saved.
     */
    const val APPEARANCE_FONTS: String = "darkness.appearance.fonts"

    /**
     * `darkness.theme.v2.favorites` — the themes the user has starred.
     *
     * A JSON array of theme names, opaque here. Its own key upstream rather than
     * a field on the selection, because starring answers "which of these do I
     * want to find again" and not "which one is on" — so it must survive, and not
     * cause, a theme change.
     *
     * Stored because the stars are reachable: every card in the theme manager
     * LunaPin mounts carries one, and a control that visibly fills in and is
     * empty again after a reload is worse than one that was never offered.
     */
    const val THEME_FAVORITES: String = "darkness.theme.v2.favorites"

    /** `darkness.sidebarState` — which sidebar sections the user has collapsed. */
    const val SIDEBAR_STATE: String = "darkness.sidebarState"

    /**
     * `darkness.sidebarWidth` — how wide the user dragged the left sidebar.
     *
     * The companion to [SIDEBAR_STATE], and a separate key upstream because that
     * one is a JSON *array* of collapsed section ids with no room in it for a
     * measurement. Stored rather than left to the browser for the same reason
     * everything else here is: the drag is a deliberate act, and a sidebar that
     * forgets it is a workspace that partly resets itself. Absent, LunaPin's own
     * seed width applies — so this key can only ever be a width somebody chose.
     */
    const val SIDEBAR_WIDTH: String = "darkness.sidebarWidth"

    /** `darkness.hotkeyBindings` — the user's own keyboard-shortcut overrides. */
    const val HOTKEY_BINDINGS: String = "darkness.hotkeyBindings"

    /** Every key the server will store. Anything else is refused. */
    val persisted: Set<String> = setOf(
        WORKSPACE,
        STARRED_FILTERS,
        LAYOUT_STATE,
        THEME_SELECTION,
        THEME_CUSTOM,
        APPEARANCE_SHAPE,
        APPEARANCE_FONTS,
        THEME_FAVORITES,
        SIDEBAR_STATE,
        SIDEBAR_WIDTH,
        HOTKEY_BINDINGS,
    )
}

/**
 * Everything the server holds for the caller, and who the caller is.
 *
 * @property userId the user these settings belong to, or null when nobody is
 *   signed in. Carried rather than inferred because the client uses it to answer
 *   "are these still the right settings?" without a second request: the shell is
 *   mounted once and can outlive a change of user. Unreachable-as-null in LPN-1,
 *   like [SessionState.user], and modelled for the same reason.
 * @property settings key to blob, holding only the keys in
 *   [UiSettingKeys.persisted]. Absent keys are absent, not empty strings: "never
 *   chosen" has to stay distinguishable from "chosen, and the value is blank",
 *   because the first means "use the default" and the second does not.
 */
@Serializable
data class UiSettingsState(
    val userId: Long? = null,
    val settings: Map<String, String> = emptyMap(),
)

/**
 * Store one setting.
 *
 * One key per request rather than the whole map, because that is how the toolkit
 * writes: a theme change is three independent `Persister.write` calls, and
 * batching them here would mean this side buffering writes it does not own and
 * guessing when the batch is complete.
 *
 * @property key must be one of [UiSettingKeys.persisted]; anything else is a 400.
 * @property value the blob, stored verbatim.
 */
@Serializable
data class SetUiSettingRequest(
    val key: String,
    val value: String,
)
